package org.teddapp.project.sync

import com.tedd.db.TeddDatabase

internal object SyncEntityOps {

    /**
     * La requête des lignes à envoyer d'une table, pour l'observer : elle émet à chaque
     * écriture locale, et c'est ce signal qui déclenche l'envoi sans attendre un battement.
     */
    fun dirtyQuery(entity: EntityKind, db: TeddDatabase): app.cash.sqldelight.Query<*> = when (entity) {
        EntityKind.Asset -> db.assetQueries.selectDirty()
        EntityKind.AssetValuation -> db.assetValuationQueries.selectDirty()
        EntityKind.Category -> db.categoryQueries.selectDirty()
        EntityKind.Competence -> db.competenceQueries.selectDirty()
        EntityKind.CompetenceCriterion -> db.competenceCriterionQueries.selectDirty()
        EntityKind.CompetenceFamily -> db.competenceFamilyQueries.selectDirty()
        EntityKind.CravingObservation -> db.cravingObservationQueries.selectDirty()
        EntityKind.ExpenseList -> db.expenseListQueries.selectDirty()
        EntityKind.ExpenseListItem -> db.expenseListItemQueries.selectDirty()
        EntityKind.FinanceAccount -> db.financeAccountQueries.selectDirty()
        EntityKind.FinanceCategory -> db.financeCategoryQueries.selectDirty()
        EntityKind.FinanceTransaction -> db.financeTransactionQueries.selectDirty()
        EntityKind.Flashcard -> db.flashcardQueries.selectDirty()
        EntityKind.FocusSession -> db.focusSessionQueries.selectDirty()
        EntityKind.Milestone -> db.milestoneQueries.selectDirty()
        EntityKind.Note -> db.noteQueries.selectDirty()
        EntityKind.Notebook -> db.notebookQueries.selectDirty()
        EntityKind.Objective -> db.objectiveQueries.selectDirty()
        EntityKind.ObjectiveReflection -> db.objectiveReflectionQueries.selectDirty()
        EntityKind.Organization -> db.organizationQueries.selectDirty()
        EntityKind.ParcoursStep -> db.parcoursStepQueries.selectDirty()
        EntityKind.PomodoroCheck -> db.pomodoroCheckQueries.selectDirty()
        EntityKind.PomodoroItem -> db.pomodoroItemQueries.selectDirty()
        EntityKind.PomodoroSession -> db.pomodoroSessionQueries.selectDirty()
        EntityKind.PortfolioPiece -> db.portfolioPieceQueries.selectDirty()
        EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote -> MindMapSyncOps.dirtyQuery(entity, db)
        EntityKind.Attachment -> FileSyncOps.dirtyQuery(entity, db)
        EntityKind.Keychain, EntityKind.KeychainItem -> VaultSyncOps.dirtyQuery(entity, db)
        EntityKind.ExpenseEntry -> ExpenseEntrySyncOps.dirtyQuery(entity, db)
        EntityKind.Simulation, EntityKind.SimulationLine -> SimulationSyncOps.dirtyQuery(entity, db)
        EntityKind.Loan, EntityKind.LoanPayment -> LoanSyncOps.dirtyQuery(entity, db)
        EntityKind.SkillResource -> SkillResourceSyncOps.dirtyQuery(entity, db)
        EntityKind.AssistantConversation, EntityKind.AssistantMessage -> AssistantSyncOps.dirtyQuery(entity, db)
        EntityKind.PortfolioPieceCompetence -> db.portfolioPieceCompetenceQueries.selectDirty()
        EntityKind.Project -> db.projectQueries.selectDirty()
        EntityKind.ProjectStatus -> db.projectStatusQueries.selectDirty()
        EntityKind.Skill -> db.skillQueries.selectDirty()
        EntityKind.Task -> db.taskQueries.selectDirty()
        EntityKind.TimerState -> db.timerStateQueries.selectDirty()
        EntityKind.UserProfile -> db.userProfileQueries.selectDirty()
    }

    fun selectDirty(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.Organization -> db.organizationQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "description" to it.description,
                "color" to it.color,
                "icon" to it.icon,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Category -> db.categoryQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "color" to it.color,
                "icon" to it.icon,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.UserProfile -> db.userProfileQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "fullname" to it.fullname,
                "email" to it.email,
                "avatar_url" to it.avatar_url,
                "bio" to it.bio,
                "language" to it.language,
                "theme_mode" to it.theme_mode,
                "finance_default_period" to it.finance_default_period,
                "finance_default_currency" to it.finance_default_currency,
                "pomodoro_focus_min" to it.pomodoro_focus_min,
                "pomodoro_short_break_min" to it.pomodoro_short_break_min,
                "pomodoro_long_break_min" to it.pomodoro_long_break_min,
                "notify_habits" to it.notify_habits,
                "notify_deadlines" to it.notify_deadlines,
                "notify_pomodoro_end" to it.notify_pomodoro_end,
                "notify_budget_alerts" to it.notify_budget_alerts,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.FinanceAccount -> db.financeAccountQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "type" to it.type,
                "currency" to it.currency,
                "balance" to it.balance,
                "icon" to it.icon,
                "color" to it.color,
                "bank" to it.bank,
                "last4" to it.last4,
                "low_threshold" to it.low_threshold,
                "high_threshold" to it.high_threshold,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.FinanceCategory -> db.financeCategoryQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "type" to it.type,
                "icon" to it.icon,
                "color" to it.color,
                "is_default" to it.is_default,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.PomodoroSession -> db.pomodoroSessionQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "date_start" to it.date_start,
                "date_end" to it.date_end,
                "status" to it.status,
                "timer_minutes" to it.timer_minutes,
                "started_at" to it.started_at,
                "ended_at" to it.ended_at,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ExpenseList -> db.expenseListQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "description" to it.description,
                "status" to it.status,
                "date" to it.date,
                "recurrence" to it.recurrence,
                "next_reset_date" to it.next_reset_date,
                "last_reset_date" to it.last_reset_date,
                "reset_mode" to it.reset_mode,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Notebook -> db.notebookQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "icon" to it.icon,
                "color" to it.color,
                "parent_id" to it.parent_id,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Note -> db.noteQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "parent_kind" to it.parent_kind,
                "parent_id" to it.parent_id,
                "title" to it.title,
                "body" to it.body,
                "color" to it.color,
                "is_pinned" to it.is_pinned,
                "is_favorite" to it.is_favorite,
                "tags" to it.tags,
                "attachments" to it.attachments,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Skill -> db.skillQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "color" to it.color,
                "icon" to it.icon,
                "intention" to it.intention,
                "target_hours" to it.target_hours,
                "tags" to it.tags,
                "pinned" to it.pinned,
                "deadline" to it.deadline,
                "archived" to it.archived,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ParcoursStep -> db.parcoursStepQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "skill_id" to it.skill_id,
                "code" to it.code,
                "kind" to it.kind,
                "position" to it.position,
                "title" to it.title,
                "summary" to it.summary,
                "planned_min" to it.planned_min,
                "level_name" to it.level_name,
                "level_desc" to it.level_desc,
                "project_id" to it.project_id,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.CompetenceFamily -> db.competenceFamilyQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "description" to it.description,
                "color" to it.color,
                "icon" to it.icon,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Competence -> db.competenceQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "family_id" to it.family_id,
                "skill_id" to it.skill_id,
                "name" to it.name,
                "description" to it.description,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.CompetenceCriterion -> db.competenceCriterionQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "competence_id" to it.competence_id,
                "label" to it.label,
                "description" to it.description,
                "level" to it.level,
                "evaluated_at" to it.evaluated_at,
                "evaluation_note" to it.evaluation_note,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.PortfolioPiece -> db.portfolioPieceQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "title" to it.title,
                "subtitle" to it.subtitle,
                "body" to it.body,
                "links" to it.links,
                "images" to it.images,
                "date_start" to it.date_start,
                "date_end" to it.date_end,
                "project_id" to it.project_id,
                "step_id" to it.step_id,
                "status" to it.status,
                "pinned" to it.pinned,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote -> MindMapSyncOps.selectDirty(entity, db)
        EntityKind.Attachment -> FileSyncOps.selectDirty(entity, db)
        EntityKind.Keychain, EntityKind.KeychainItem -> VaultSyncOps.selectDirty(entity, db)
        EntityKind.ExpenseEntry -> ExpenseEntrySyncOps.selectDirty(entity, db)
        EntityKind.Simulation, EntityKind.SimulationLine -> SimulationSyncOps.selectDirty(entity, db)
        EntityKind.Loan, EntityKind.LoanPayment -> LoanSyncOps.selectDirty(entity, db)
        EntityKind.SkillResource -> SkillResourceSyncOps.selectDirty(entity, db)
        EntityKind.AssistantConversation, EntityKind.AssistantMessage -> AssistantSyncOps.selectDirty(entity, db)
        EntityKind.PortfolioPieceCompetence -> db.portfolioPieceCompetenceQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "piece_id" to it.piece_id,
                "competence_id" to it.competence_id,
                "criterion_id" to it.criterion_id,
                "note" to it.note,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Asset -> db.assetQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "kind" to it.kind,
                "currency" to it.currency,
                "value" to it.value_,
                "purchase_price" to it.purchase_price,
                "acquired_date" to it.acquired_date,
                "valued_at" to it.valued_at,
                "note" to it.note,
                "archived" to it.archived,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.AssetValuation -> db.assetValuationQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "asset_id" to it.asset_id,
                "date" to it.date,
                "value" to it.value_,
                "note" to it.note,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Flashcard -> db.flashcardQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "skill_id" to it.skill_id,
                "front" to it.front,
                "back" to it.back,
                "note" to it.note,
                "box" to it.box,
                "due_date" to it.due_date,
                "last_review" to it.last_review,
                "reviews" to it.reviews,
                "lapses" to it.lapses,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ObjectiveReflection -> db.objectiveReflectionQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "objective_id" to it.objective_id,
                "attempt" to it.attempt,
                "text" to it.text,
                "missed_deadline" to it.missed_deadline,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.CravingObservation -> db.cravingObservationQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "habit" to it.habit,
                "date" to it.date,
                "sensations" to it.sensations,
                "zones" to it.zones,
                "intensity" to it.intensity,
                "outcome" to it.outcome,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.TimerState -> db.timerStateQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "running" to it.running,
                "started_at" to it.started_at,
                "planned_minutes" to it.planned_minutes,
                "item_id" to it.item_id,
                "skill_id" to it.skill_id,
                "paused_at" to it.paused_at,
                "paused_accum_ms" to it.paused_accum_ms,
                "device" to it.device,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.FocusSession -> db.focusSessionQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "started_at" to it.started_at,
                "ended_at" to it.ended_at,
                "focus_minutes" to it.focus_minutes,
                "break_minutes" to it.break_minutes,
                "pomodoros" to it.pomodoros,
                "task_id" to it.task_id,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Objective -> db.objectiveQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "category_id" to it.category_id,
                "title" to it.title,
                "description" to it.description,
                "motivation" to it.motivation,
                "skill_id" to it.skill_id,
                "step_id" to it.step_id,
                "deadline" to it.deadline,
                "year" to it.year,
                "status" to it.status,
                "progress" to it.progress,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Project -> db.projectQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "organization_id" to it.organization_id,
                "objective_id" to it.objective_id,
                "name" to it.name,
                "description" to it.description,
                "objectives" to it.objectives,
                "skill_id" to it.skill_id,
                "color" to it.color,
                "icon" to it.icon,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ProjectStatus -> db.projectStatusQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "project_id" to it.project_id,
                "name" to it.name,
                "color" to it.color,
                "order_index" to it.order_index,
                "is_terminal" to it.is_terminal,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Milestone -> db.milestoneQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "objective_id" to it.objective_id,
                "title" to it.title,
                "description" to it.description,
                "deadline" to it.deadline,
                "progress" to it.progress,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Task -> db.taskQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "parent_kind" to it.parent_kind,
                "parent_id" to it.parent_id,
                "status_id" to it.status_id,
                "title" to it.title,
                "description" to it.description,
                "status" to it.status,
                "priority" to it.priority,
                "effort" to it.effort,
                "due_date" to it.due_date,
                "estimated_minutes" to it.estimated_minutes,
                "real_minutes" to it.real_minutes,
                "recurrence" to it.recurrence,
                "pomodoro_pool_date" to it.pomodoro_pool_date,
                "kind" to it.kind,
                "deliverable" to it.deliverable,
                "acceptance" to it.acceptance,
                "order_index" to it.order_index,
                "completed_at" to it.completed_at,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.FinanceTransaction -> db.financeTransactionQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "date" to it.date,
                "account_id" to it.account_id,
                "to_account_id" to it.to_account_id,
                "cat_id" to it.cat_id,
                "type" to it.type,
                "amount" to it.amount,
                "description" to it.description,
                "fund_id" to it.fund_id,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ExpenseListItem -> db.expenseListItemQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "list_id" to it.list_id,
                "name" to it.name,
                "planned" to it.planned,
                "actual" to it.actual_,
                "cat_id" to it.cat_id,
                "is_done" to it.is_done,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.PomodoroItem -> db.pomodoroItemQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "session_id" to it.session_id,
                "source_type" to it.source_type,
                "source_id" to it.source_id,
                "custom_title" to it.custom_title,
                "priority" to it.priority,
                "order_index" to it.order_index,
                "completed" to it.completed,
                "completed_at" to it.completed_at,
                "estimated_minutes" to it.estimated_minutes,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.PomodoroCheck -> db.pomodoroCheckQueries.selectDirty().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "item_id" to it.item_id,
                "label" to it.label,
                "note" to it.note,
                "checked" to it.checked,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
    }

    fun selectAllForExport(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.Organization -> db.organizationQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "description" to it.description,
                "color" to it.color,
                "icon" to it.icon,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Category -> db.categoryQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "color" to it.color,
                "icon" to it.icon,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.UserProfile -> db.userProfileQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "fullname" to it.fullname,
                "email" to it.email,
                "avatar_url" to it.avatar_url,
                "bio" to it.bio,
                "language" to it.language,
                "theme_mode" to it.theme_mode,
                "finance_default_period" to it.finance_default_period,
                "finance_default_currency" to it.finance_default_currency,
                "pomodoro_focus_min" to it.pomodoro_focus_min,
                "pomodoro_short_break_min" to it.pomodoro_short_break_min,
                "pomodoro_long_break_min" to it.pomodoro_long_break_min,
                "notify_habits" to it.notify_habits,
                "notify_deadlines" to it.notify_deadlines,
                "notify_pomodoro_end" to it.notify_pomodoro_end,
                "notify_budget_alerts" to it.notify_budget_alerts,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.FinanceAccount -> db.financeAccountQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "type" to it.type,
                "currency" to it.currency,
                "balance" to it.balance,
                "icon" to it.icon,
                "color" to it.color,
                "bank" to it.bank,
                "last4" to it.last4,
                "low_threshold" to it.low_threshold,
                "high_threshold" to it.high_threshold,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.FinanceCategory -> db.financeCategoryQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "type" to it.type,
                "icon" to it.icon,
                "color" to it.color,
                "is_default" to it.is_default,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.PomodoroSession -> db.pomodoroSessionQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "date_start" to it.date_start,
                "date_end" to it.date_end,
                "status" to it.status,
                "timer_minutes" to it.timer_minutes,
                "started_at" to it.started_at,
                "ended_at" to it.ended_at,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ExpenseList -> db.expenseListQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "description" to it.description,
                "status" to it.status,
                "date" to it.date,
                "recurrence" to it.recurrence,
                "next_reset_date" to it.next_reset_date,
                "last_reset_date" to it.last_reset_date,
                "reset_mode" to it.reset_mode,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Notebook -> db.notebookQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "icon" to it.icon,
                "color" to it.color,
                "parent_id" to it.parent_id,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Note -> db.noteQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "parent_kind" to it.parent_kind,
                "parent_id" to it.parent_id,
                "title" to it.title,
                "body" to it.body,
                "color" to it.color,
                "is_pinned" to it.is_pinned,
                "is_favorite" to it.is_favorite,
                "tags" to it.tags,
                "attachments" to it.attachments,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Skill -> db.skillQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "color" to it.color,
                "icon" to it.icon,
                "intention" to it.intention,
                "target_hours" to it.target_hours,
                "tags" to it.tags,
                "pinned" to it.pinned,
                "deadline" to it.deadline,
                "archived" to it.archived,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ParcoursStep -> db.parcoursStepQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "skill_id" to it.skill_id,
                "code" to it.code,
                "kind" to it.kind,
                "position" to it.position,
                "title" to it.title,
                "summary" to it.summary,
                "planned_min" to it.planned_min,
                "level_name" to it.level_name,
                "level_desc" to it.level_desc,
                "project_id" to it.project_id,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.CompetenceFamily -> db.competenceFamilyQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "description" to it.description,
                "color" to it.color,
                "icon" to it.icon,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Competence -> db.competenceQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "family_id" to it.family_id,
                "skill_id" to it.skill_id,
                "name" to it.name,
                "description" to it.description,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.CompetenceCriterion -> db.competenceCriterionQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "competence_id" to it.competence_id,
                "label" to it.label,
                "description" to it.description,
                "level" to it.level,
                "evaluated_at" to it.evaluated_at,
                "evaluation_note" to it.evaluation_note,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.PortfolioPiece -> db.portfolioPieceQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "title" to it.title,
                "subtitle" to it.subtitle,
                "body" to it.body,
                "links" to it.links,
                "images" to it.images,
                "date_start" to it.date_start,
                "date_end" to it.date_end,
                "project_id" to it.project_id,
                "step_id" to it.step_id,
                "status" to it.status,
                "pinned" to it.pinned,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote -> MindMapSyncOps.selectAll(entity, db)
        EntityKind.Attachment -> FileSyncOps.selectAll(entity, db)
        EntityKind.Keychain, EntityKind.KeychainItem -> VaultSyncOps.selectAll(entity, db)
        EntityKind.ExpenseEntry -> ExpenseEntrySyncOps.selectAll(entity, db)
        EntityKind.Simulation, EntityKind.SimulationLine -> SimulationSyncOps.selectAll(entity, db)
        EntityKind.Loan, EntityKind.LoanPayment -> LoanSyncOps.selectAll(entity, db)
        EntityKind.SkillResource -> SkillResourceSyncOps.selectAll(entity, db)
        EntityKind.AssistantConversation, EntityKind.AssistantMessage -> AssistantSyncOps.selectAll(entity, db)
        EntityKind.PortfolioPieceCompetence -> db.portfolioPieceCompetenceQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "piece_id" to it.piece_id,
                "competence_id" to it.competence_id,
                "criterion_id" to it.criterion_id,
                "note" to it.note,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Asset -> db.assetQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "name" to it.name,
                "kind" to it.kind,
                "currency" to it.currency,
                "value" to it.value_,
                "purchase_price" to it.purchase_price,
                "acquired_date" to it.acquired_date,
                "valued_at" to it.valued_at,
                "note" to it.note,
                "archived" to it.archived,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.AssetValuation -> db.assetValuationQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "asset_id" to it.asset_id,
                "date" to it.date,
                "value" to it.value_,
                "note" to it.note,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Flashcard -> db.flashcardQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "skill_id" to it.skill_id,
                "front" to it.front,
                "back" to it.back,
                "note" to it.note,
                "box" to it.box,
                "due_date" to it.due_date,
                "last_review" to it.last_review,
                "reviews" to it.reviews,
                "lapses" to it.lapses,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ObjectiveReflection -> db.objectiveReflectionQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "objective_id" to it.objective_id,
                "attempt" to it.attempt,
                "text" to it.text,
                "missed_deadline" to it.missed_deadline,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.CravingObservation -> db.cravingObservationQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "habit" to it.habit,
                "date" to it.date,
                "sensations" to it.sensations,
                "zones" to it.zones,
                "intensity" to it.intensity,
                "outcome" to it.outcome,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.TimerState -> db.timerStateQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "running" to it.running,
                "started_at" to it.started_at,
                "planned_minutes" to it.planned_minutes,
                "item_id" to it.item_id,
                "skill_id" to it.skill_id,
                "paused_at" to it.paused_at,
                "paused_accum_ms" to it.paused_accum_ms,
                "device" to it.device,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.FocusSession -> db.focusSessionQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "started_at" to it.started_at,
                "ended_at" to it.ended_at,
                "focus_minutes" to it.focus_minutes,
                "break_minutes" to it.break_minutes,
                "pomodoros" to it.pomodoros,
                "task_id" to it.task_id,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Objective -> db.objectiveQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "category_id" to it.category_id,
                "title" to it.title,
                "description" to it.description,
                "motivation" to it.motivation,
                "skill_id" to it.skill_id,
                "step_id" to it.step_id,
                "deadline" to it.deadline,
                "year" to it.year,
                "status" to it.status,
                "progress" to it.progress,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Project -> db.projectQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "organization_id" to it.organization_id,
                "objective_id" to it.objective_id,
                "name" to it.name,
                "description" to it.description,
                "objectives" to it.objectives,
                "skill_id" to it.skill_id,
                "color" to it.color,
                "icon" to it.icon,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ProjectStatus -> db.projectStatusQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "project_id" to it.project_id,
                "name" to it.name,
                "color" to it.color,
                "order_index" to it.order_index,
                "is_terminal" to it.is_terminal,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Milestone -> db.milestoneQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "objective_id" to it.objective_id,
                "title" to it.title,
                "description" to it.description,
                "deadline" to it.deadline,
                "progress" to it.progress,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.Task -> db.taskQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "parent_kind" to it.parent_kind,
                "parent_id" to it.parent_id,
                "status_id" to it.status_id,
                "title" to it.title,
                "description" to it.description,
                "status" to it.status,
                "priority" to it.priority,
                "effort" to it.effort,
                "due_date" to it.due_date,
                "estimated_minutes" to it.estimated_minutes,
                "real_minutes" to it.real_minutes,
                "recurrence" to it.recurrence,
                "pomodoro_pool_date" to it.pomodoro_pool_date,
                "kind" to it.kind,
                "deliverable" to it.deliverable,
                "acceptance" to it.acceptance,
                "order_index" to it.order_index,
                "completed_at" to it.completed_at,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.FinanceTransaction -> db.financeTransactionQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "date" to it.date,
                "account_id" to it.account_id,
                "to_account_id" to it.to_account_id,
                "cat_id" to it.cat_id,
                "type" to it.type,
                "amount" to it.amount,
                "description" to it.description,
                "fund_id" to it.fund_id,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.ExpenseListItem -> db.expenseListItemQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "list_id" to it.list_id,
                "name" to it.name,
                "planned" to it.planned,
                "actual" to it.actual_,
                "cat_id" to it.cat_id,
                "is_done" to it.is_done,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.PomodoroItem -> db.pomodoroItemQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "session_id" to it.session_id,
                "source_type" to it.source_type,
                "source_id" to it.source_id,
                "custom_title" to it.custom_title,
                "priority" to it.priority,
                "order_index" to it.order_index,
                "completed" to it.completed,
                "completed_at" to it.completed_at,
                "estimated_minutes" to it.estimated_minutes,
                "created_at" to it.created_at,
            ))
        }
        EntityKind.PomodoroCheck -> db.pomodoroCheckQueries.selectAll().executeAsList().map {
            RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
                "item_id" to it.item_id,
                "label" to it.label,
                "note" to it.note,
                "checked" to it.checked,
                "order_index" to it.order_index,
                "created_at" to it.created_at,
            ))
        }
    }

    /**
     * Compte rapide des rows actives (is_deleted = 0) pour une entité.
     * Utilisé pour les stats Backup (Volume, Modules count). On charge la liste
     * pour compter — acceptable vu les volumes locaux modestes.
     */
    fun countAll(entity: EntityKind, db: TeddDatabase): Int = selectAllForExport(entity, db).size

    fun markSynced(entity: EntityKind, db: TeddDatabase, id: String, updatedAt: Long, syncedAt: Long) {
        when (entity) {
            EntityKind.Organization -> db.organizationQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Category -> db.categoryQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.UserProfile -> db.userProfileQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.FinanceAccount -> db.financeAccountQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.FinanceCategory -> db.financeCategoryQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.PomodoroSession -> db.pomodoroSessionQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.ExpenseList -> db.expenseListQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Notebook -> db.notebookQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Note -> db.noteQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Skill -> db.skillQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.ParcoursStep -> db.parcoursStepQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.CompetenceFamily -> db.competenceFamilyQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Competence -> db.competenceQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.CompetenceCriterion -> db.competenceCriterionQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.PortfolioPiece -> db.portfolioPieceQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote -> MindMapSyncOps.markSynced(entity, db, id, updatedAt, syncedAt)
            EntityKind.Attachment -> FileSyncOps.markSynced(entity, db, id, updatedAt, syncedAt)
            EntityKind.Keychain, EntityKind.KeychainItem -> VaultSyncOps.markSynced(entity, db, id, updatedAt, syncedAt)
            EntityKind.ExpenseEntry -> ExpenseEntrySyncOps.markSynced(entity, db, id, updatedAt, syncedAt)
            EntityKind.Simulation, EntityKind.SimulationLine -> SimulationSyncOps.markSynced(entity, db, id, updatedAt, syncedAt)
            EntityKind.Loan, EntityKind.LoanPayment -> LoanSyncOps.markSynced(entity, db, id, updatedAt, syncedAt)
            EntityKind.SkillResource -> SkillResourceSyncOps.markSynced(entity, db, id, updatedAt, syncedAt)
            EntityKind.AssistantConversation, EntityKind.AssistantMessage -> AssistantSyncOps.markSynced(entity, db, id, updatedAt, syncedAt)
            EntityKind.PortfolioPieceCompetence -> db.portfolioPieceCompetenceQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Asset -> db.assetQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.AssetValuation -> db.assetValuationQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Flashcard -> db.flashcardQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.ObjectiveReflection -> db.objectiveReflectionQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.CravingObservation -> db.cravingObservationQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.TimerState -> db.timerStateQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.FocusSession -> db.focusSessionQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Objective -> db.objectiveQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Project -> db.projectQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.ProjectStatus -> db.projectStatusQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Milestone -> db.milestoneQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.Task -> db.taskQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.FinanceTransaction -> db.financeTransactionQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.ExpenseListItem -> db.expenseListItemQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.PomodoroItem -> db.pomodoroItemQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.PomodoroCheck -> db.pomodoroCheckQueries.markSynced(syncedAt, id, updatedAt)
        }
    }

    fun selectByIdAnyState(entity: EntityKind, db: TeddDatabase, id: String): LocalSnapshot? = when (entity) {
        EntityKind.Organization -> db.organizationQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Category -> db.categoryQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.UserProfile -> db.userProfileQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.FinanceAccount -> db.financeAccountQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.FinanceCategory -> db.financeCategoryQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.PomodoroSession -> db.pomodoroSessionQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.ExpenseList -> db.expenseListQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Notebook -> db.notebookQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Note -> db.noteQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Skill -> db.skillQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.ParcoursStep -> db.parcoursStepQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.CompetenceFamily -> db.competenceFamilyQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Competence -> db.competenceQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.CompetenceCriterion -> db.competenceCriterionQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.PortfolioPiece -> db.portfolioPieceQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote -> MindMapSyncOps.selectByIdAnyState(entity, db, id)
        EntityKind.Attachment -> FileSyncOps.selectByIdAnyState(entity, db, id)
        EntityKind.Keychain, EntityKind.KeychainItem -> VaultSyncOps.selectByIdAnyState(entity, db, id)
        EntityKind.ExpenseEntry -> ExpenseEntrySyncOps.selectByIdAnyState(entity, db, id)
        EntityKind.Simulation, EntityKind.SimulationLine -> SimulationSyncOps.selectByIdAnyState(entity, db, id)
        EntityKind.Loan, EntityKind.LoanPayment -> LoanSyncOps.selectByIdAnyState(entity, db, id)
        EntityKind.SkillResource -> SkillResourceSyncOps.selectByIdAnyState(entity, db, id)
        EntityKind.AssistantConversation, EntityKind.AssistantMessage -> AssistantSyncOps.selectByIdAnyState(entity, db, id)
        EntityKind.PortfolioPieceCompetence -> db.portfolioPieceCompetenceQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Asset -> db.assetQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.AssetValuation -> db.assetValuationQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Flashcard -> db.flashcardQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.ObjectiveReflection -> db.objectiveReflectionQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.CravingObservation -> db.cravingObservationQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.TimerState -> db.timerStateQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.FocusSession -> db.focusSessionQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Objective -> db.objectiveQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Project -> db.projectQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.ProjectStatus -> db.projectStatusQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Milestone -> db.milestoneQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.Task -> db.taskQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.FinanceTransaction -> db.financeTransactionQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.ExpenseListItem -> db.expenseListItemQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.PomodoroItem -> db.pomodoroItemQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.PomodoroCheck -> db.pomodoroCheckQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
    }

    fun applyRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val existing = selectByIdAnyState(entity, db, row.id) != null
        if (existing) updateFromRemote(entity, db, row) else insertFromRemote(entity, db, row)
        if (row.isDeleted) softDeleteFromRemote(entity, db, row)
        else if (existing) restoreIfDeleted(entity, db, row)
    }

    private fun insertFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.Organization -> db.organizationQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("description"),
                p.str("color") ?: "",
                p.str("icon") ?: "",
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Category -> db.categoryQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("color") ?: "",
                p.str("icon") ?: "",
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.UserProfile -> db.userProfileQueries.insert(
                row.id,
                p.str("fullname") ?: "",
                p.str("email") ?: "",
                p.str("avatar_url") ?: "",
                p.str("bio") ?: "",
                p.str("language") ?: "fr",
                p.str("theme_mode") ?: "System",
                p.str("finance_default_period") ?: "all",
                p.str("finance_default_currency") ?: "XOF",
                p.long("pomodoro_focus_min") ?: 25L,
                p.long("pomodoro_short_break_min") ?: 5L,
                p.long("pomodoro_long_break_min") ?: 15L,
                p.long("notify_habits") ?: 1L,
                p.long("notify_deadlines") ?: 1L,
                p.long("notify_pomodoro_end") ?: 1L,
                p.long("notify_budget_alerts") ?: 1L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.FinanceAccount -> db.financeAccountQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("type") ?: "",
                p.str("currency") ?: "",
                p.long("balance") ?: 0L,
                p.str("icon") ?: "",
                p.str("color") ?: "",
                p.str("bank"),
                p.str("last4"),
                p.long("low_threshold"),
                p.long("high_threshold"),
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.FinanceCategory -> db.financeCategoryQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("type") ?: "",
                p.str("icon") ?: "",
                p.str("color") ?: "",
                p.long("is_default") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.PomodoroSession -> db.pomodoroSessionQueries.insert(
                row.id,
                p.str("date_start") ?: "",
                p.str("date_end"),
                p.str("status") ?: "",
                p.long("timer_minutes") ?: 0L,
                p.long("started_at") ?: 0L,
                p.long("ended_at"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.ExpenseList -> db.expenseListQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("description"),
                p.str("status") ?: "",
                p.str("date"),
                p.str("recurrence") ?: "once",
                p.str("next_reset_date"),
                p.str("last_reset_date"),
                p.str("reset_mode") ?: "reset",
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Notebook -> db.notebookQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("icon") ?: "",
                p.str("color") ?: "",
                p.str("parent_id"),
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Note -> db.noteQueries.insert(
                row.id,
                p.str("parent_kind") ?: "",
                p.str("parent_id"),
                p.str("title"),
                p.str("body") ?: "",
                p.str("color"),
                p.long("is_pinned") ?: 0L,
                p.long("is_favorite") ?: 0L,
                p.str("tags"),
                p.str("attachments"),
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Skill -> db.skillQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("color") ?: "#22508A",
                p.str("icon") ?: "book",
                p.str("intention"),
                p.long("target_hours") ?: 1000L,
                p.str("deadline"),
                p.str("tags"),
                p.long("pinned") ?: 0L,
                p.long("archived") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.ParcoursStep -> db.parcoursStepQueries.insert(
                row.id,
                p.str("skill_id") ?: "",
                p.str("code") ?: "",
                p.str("kind") ?: "module",
                p.long("position") ?: 0L,
                p.str("title") ?: "",
                p.str("summary"),
                p.long("planned_min") ?: 0L,
                p.str("level_name"),
                p.str("level_desc"),
                p.str("project_id"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.CompetenceFamily -> db.competenceFamilyQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("description"),
                p.str("color") ?: "#22508A",
                p.str("icon") ?: "book",
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Competence -> db.competenceQueries.insert(
                row.id,
                p.str("family_id") ?: "",
                p.str("skill_id"),
                p.str("name") ?: "",
                p.str("description"),
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.CompetenceCriterion -> db.competenceCriterionQueries.insert(
                row.id,
                p.str("competence_id") ?: "",
                p.str("label") ?: "",
                p.str("description"),
                p.long("level") ?: 0L,
                p.long("evaluated_at"),
                p.str("evaluation_note"),
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.PortfolioPiece -> db.portfolioPieceQueries.insert(
                row.id,
                p.str("title") ?: "",
                p.str("subtitle"),
                p.str("body") ?: "",
                p.str("links"),
                p.str("images"),
                p.str("date_start"),
                p.str("date_end"),
                p.str("project_id"),
                p.str("step_id"),
                p.str("status") ?: "draft",
                p.long("pinned") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote -> MindMapSyncOps.insertFromRemote(entity, db, row)
            EntityKind.Attachment -> FileSyncOps.insertFromRemote(entity, db, row)
            EntityKind.Keychain, EntityKind.KeychainItem -> VaultSyncOps.insertFromRemote(entity, db, row)
            EntityKind.ExpenseEntry -> ExpenseEntrySyncOps.insertFromRemote(entity, db, row)
            EntityKind.Simulation, EntityKind.SimulationLine -> SimulationSyncOps.insertFromRemote(entity, db, row)
            EntityKind.Loan, EntityKind.LoanPayment -> LoanSyncOps.insertFromRemote(entity, db, row)
            EntityKind.SkillResource -> SkillResourceSyncOps.insertFromRemote(entity, db, row)
            EntityKind.AssistantConversation, EntityKind.AssistantMessage -> AssistantSyncOps.insertFromRemote(entity, db, row)
            EntityKind.PortfolioPieceCompetence -> db.portfolioPieceCompetenceQueries.insert(
                row.id,
                p.str("piece_id") ?: "",
                p.str("competence_id") ?: "",
                p.str("criterion_id"),
                p.str("note"),
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Asset -> db.assetQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("kind") ?: "other",
                p.str("currency") ?: "XOF",
                p.long("value"),
                p.long("purchase_price"),
                p.str("acquired_date"),
                p.str("valued_at"),
                p.str("note"),
                p.long("archived") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.AssetValuation -> db.assetValuationQueries.insert(
                row.id,
                p.str("asset_id") ?: "",
                p.str("date") ?: "",
                p.long("value") ?: 0L,
                p.str("note"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Flashcard -> db.flashcardQueries.insert(
                row.id,
                p.str("skill_id"),
                p.str("front") ?: "",
                p.str("back") ?: "",
                p.str("note"),
                p.long("box") ?: 1L,
                p.str("due_date") ?: "",
                p.str("last_review"),
                p.long("reviews") ?: 0L,
                p.long("lapses") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.ObjectiveReflection -> db.objectiveReflectionQueries.insert(
                row.id,
                p.str("objective_id") ?: "",
                p.long("attempt") ?: 1L,
                p.str("text") ?: "",
                p.str("missed_deadline"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.CravingObservation -> db.cravingObservationQueries.insert(
                row.id,
                p.str("habit") ?: "",
                p.str("date") ?: "",
                p.str("sensations") ?: "",
                p.str("zones") ?: "",
                p.long("intensity") ?: 5L,
                p.str("outcome") ?: "crossed",
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            // Un fait venu d'ailleurs entre avec applied = 0 : il reste à rejouer ici.
            EntityKind.TimerState -> db.timerStateQueries.insert(
                row.id,
                p.long("running") ?: 0L,
                p.long("started_at"),
                p.long("planned_minutes") ?: 25L,
                p.str("item_id"),
                p.str("skill_id"),
                p.long("paused_at"),
                p.long("paused_accum_ms") ?: 0L,
                p.str("device"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.FocusSession -> db.focusSessionQueries.insert(
                row.id,
                p.long("started_at") ?: 0L,
                p.long("ended_at") ?: 0L,
                p.long("focus_minutes") ?: 0L,
                p.long("break_minutes") ?: 0L,
                p.long("pomodoros") ?: 0L,
                p.str("task_id"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Objective -> {
                db.objectiveQueries.insert(
                    row.id,
                    p.str("category_id"),
                    p.str("title") ?: "",
                    p.str("description"),
                    p.str("motivation"),
                    p.str("deadline"),
                    p.long("year"),
                    p.str("status") ?: "",
                    p.long("progress") ?: 0L,
                    p.long("created_at") ?: row.updatedAt,
                    row.updatedAt,
                )
                db.objectiveQueries.setSkillAndStep(p.str("skill_id"), p.str("step_id"), row.id)
            }
            EntityKind.Project -> {
                db.projectQueries.insert(
                    row.id,
                    p.str("organization_id") ?: "",
                    p.str("objective_id"),
                    p.str("name") ?: "",
                    p.str("description"),
                    p.str("objectives"),
                    p.str("color") ?: "",
                    p.str("icon") ?: "",
                    p.long("order_index") ?: 0L,
                    p.long("created_at") ?: row.updatedAt,
                    row.updatedAt,
                )
                db.projectQueries.setSkill(p.str("skill_id"), row.id)
            }
            EntityKind.ProjectStatus -> db.projectStatusQueries.insert(
                row.id,
                p.str("project_id") ?: "",
                p.str("name") ?: "",
                p.str("color") ?: "",
                p.long("order_index") ?: 0L,
                p.long("is_terminal") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Milestone -> db.milestoneQueries.insert(
                row.id,
                p.str("objective_id") ?: "",
                p.str("title") ?: "",
                p.str("description"),
                p.str("deadline"),
                p.long("progress") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.Task -> {
                db.taskQueries.insert(
                    row.id,
                    p.str("parent_kind") ?: "",
                    p.str("parent_id"),
                    p.str("status_id"),
                    p.str("title") ?: "",
                    p.str("description"),
                    p.str("status") ?: "",
                    p.str("priority"),
                    p.long("effort"),
                    p.str("due_date"),
                    p.long("estimated_minutes"),
                    p.long("real_minutes"),
                    p.str("recurrence"),
                    p.str("pomodoro_pool_date"),
                    p.long("order_index") ?: 0L,
                    p.long("completed_at"),
                    p.long("created_at") ?: row.updatedAt,
                    row.updatedAt,
                )
                db.taskQueries.setParcoursFields(p.str("kind"), p.str("deliverable"), p.str("acceptance"), row.id)
            }
            EntityKind.FinanceTransaction -> db.financeTransactionQueries.insert(
                row.id,
                p.str("date") ?: "",
                p.str("account_id") ?: "",
                p.str("to_account_id"),
                p.str("cat_id"),
                p.str("type") ?: "",
                p.long("amount") ?: 0L,
                p.str("description"),
                p.str("fund_id"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.ExpenseListItem -> db.expenseListItemQueries.insert(
                row.id,
                p.str("list_id") ?: "",
                p.str("name") ?: "",
                p.long("planned") ?: 0L,
                p.long("actual"),
                p.str("cat_id"),
                p.long("is_done") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.PomodoroItem -> db.pomodoroItemQueries.insert(
                row.id,
                p.str("session_id") ?: "",
                p.str("source_type") ?: "",
                p.str("source_id") ?: "",
                p.str("custom_title"),
                p.long("priority") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("completed") ?: 0L,
                p.long("completed_at"),
                p.long("estimated_minutes"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.PomodoroCheck -> db.pomodoroCheckQueries.insert(
                row.id,
                p.str("item_id") ?: "",
                p.str("label") ?: "",
                p.str("note"),
                p.long("checked") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
        }
    }

    private fun updateFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.Organization -> db.organizationQueries.update(
                p.str("name") ?: "",
                p.str("description"),
                p.str("color") ?: "",
                p.str("icon") ?: "",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.Category -> db.categoryQueries.update(
                p.str("name") ?: "",
                p.str("color") ?: "",
                p.str("icon") ?: "",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.UserProfile -> db.userProfileQueries.update(
                p.str("fullname") ?: "",
                p.str("email") ?: "",
                p.str("avatar_url") ?: "",
                p.str("bio") ?: "",
                p.str("language") ?: "fr",
                p.str("theme_mode") ?: "System",
                p.str("finance_default_period") ?: "all",
                p.str("finance_default_currency") ?: "XOF",
                p.long("pomodoro_focus_min") ?: 25L,
                p.long("pomodoro_short_break_min") ?: 5L,
                p.long("pomodoro_long_break_min") ?: 15L,
                p.long("notify_habits") ?: 1L,
                p.long("notify_deadlines") ?: 1L,
                p.long("notify_pomodoro_end") ?: 1L,
                p.long("notify_budget_alerts") ?: 1L,
                row.updatedAt,
                row.id,
            )
            EntityKind.FinanceAccount -> db.financeAccountQueries.updateFromRemote(
                p.str("name") ?: "",
                p.str("type") ?: "",
                p.str("currency") ?: "",
                p.long("balance") ?: 0L,
                p.str("icon") ?: "",
                p.str("color") ?: "",
                p.str("bank"),
                p.str("last4"),
                p.long("low_threshold"),
                p.long("high_threshold"),
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.FinanceCategory -> db.financeCategoryQueries.update(
                p.str("name") ?: "",
                p.str("type") ?: "",
                p.str("icon") ?: "",
                p.str("color") ?: "",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.PomodoroSession -> db.pomodoroSessionQueries.update(
                p.str("date_start") ?: "",
                p.str("date_end"),
                p.str("status") ?: "",
                p.long("timer_minutes") ?: 0L,
                p.long("started_at") ?: 0L,
                p.long("ended_at"),
                p.long("created_at") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.ExpenseList -> db.expenseListQueries.updateFromRemote(
                p.str("name") ?: "",
                p.str("description"),
                p.str("status") ?: "",
                p.str("date"),
                p.str("recurrence") ?: "once",
                p.str("next_reset_date"),
                p.str("last_reset_date"),
                p.str("reset_mode") ?: "reset",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.Notebook -> db.notebookQueries.update(
                p.str("name") ?: "",
                p.str("icon") ?: "",
                p.str("color") ?: "",
                p.str("parent_id"),
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.Note -> db.noteQueries.update(
                p.str("parent_kind") ?: "",
                p.str("parent_id"),
                p.str("title"),
                p.str("body") ?: "",
                p.str("color"),
                p.long("is_pinned") ?: 0L,
                p.long("is_favorite") ?: 0L,
                p.str("tags"),
                p.str("attachments"),
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.Skill -> db.skillQueries.update(
                p.str("name") ?: "",
                p.str("color") ?: "#22508A",
                p.str("icon") ?: "book",
                p.str("intention"),
                p.long("target_hours") ?: 1000L,
                p.str("deadline"),
                p.str("tags"),
                p.long("pinned") ?: 0L,
                p.long("archived") ?: 0L,
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.ParcoursStep -> db.parcoursStepQueries.update(
                p.str("skill_id") ?: "",
                p.str("code") ?: "",
                p.str("kind") ?: "module",
                p.long("position") ?: 0L,
                p.str("title") ?: "",
                p.str("summary"),
                p.long("planned_min") ?: 0L,
                p.str("level_name"),
                p.str("level_desc"),
                p.str("project_id"),
                row.updatedAt,
                row.id,
            )
            EntityKind.CompetenceFamily -> db.competenceFamilyQueries.update(
                p.str("name") ?: "",
                p.str("description"),
                p.str("color") ?: "#22508A",
                p.str("icon") ?: "book",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.Competence -> db.competenceQueries.update(
                p.str("family_id") ?: "",
                p.str("skill_id"),
                p.str("name") ?: "",
                p.str("description"),
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.CompetenceCriterion -> db.competenceCriterionQueries.update(
                p.str("competence_id") ?: "",
                p.str("label") ?: "",
                p.str("description"),
                p.long("level") ?: 0L,
                p.long("evaluated_at"),
                p.str("evaluation_note"),
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.PortfolioPiece -> db.portfolioPieceQueries.update(
                p.str("title") ?: "",
                p.str("subtitle"),
                p.str("body") ?: "",
                p.str("links"),
                p.str("images"),
                p.str("date_start"),
                p.str("date_end"),
                p.str("project_id"),
                p.str("step_id"),
                p.str("status") ?: "draft",
                p.long("pinned") ?: 0L,
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote -> MindMapSyncOps.updateFromRemote(entity, db, row)
            EntityKind.Attachment -> FileSyncOps.updateFromRemote(entity, db, row)
            EntityKind.Keychain, EntityKind.KeychainItem -> VaultSyncOps.updateFromRemote(entity, db, row)
            EntityKind.ExpenseEntry -> ExpenseEntrySyncOps.updateFromRemote(entity, db, row)
            EntityKind.Simulation, EntityKind.SimulationLine -> SimulationSyncOps.updateFromRemote(entity, db, row)
            EntityKind.Loan, EntityKind.LoanPayment -> LoanSyncOps.updateFromRemote(entity, db, row)
            EntityKind.SkillResource -> SkillResourceSyncOps.updateFromRemote(entity, db, row)
            EntityKind.AssistantConversation, EntityKind.AssistantMessage -> AssistantSyncOps.updateFromRemote(entity, db, row)
            EntityKind.PortfolioPieceCompetence -> db.portfolioPieceCompetenceQueries.update(
                p.str("piece_id") ?: "",
                p.str("competence_id") ?: "",
                p.str("criterion_id"),
                p.str("note"),
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.Asset -> db.assetQueries.update(
                p.str("name") ?: "",
                p.str("kind") ?: "other",
                p.str("currency") ?: "XOF",
                p.long("value"),
                p.long("purchase_price"),
                p.str("acquired_date"),
                p.str("valued_at"),
                p.str("note"),
                p.long("archived") ?: 0L,
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.AssetValuation -> db.assetValuationQueries.update(
                p.str("asset_id") ?: "",
                p.str("date") ?: "",
                p.long("value") ?: 0L,
                p.str("note"),
                row.updatedAt,
                row.id,
            )
            EntityKind.Flashcard -> db.flashcardQueries.syncUpdate(
                p.str("skill_id"),
                p.str("front") ?: "",
                p.str("back") ?: "",
                p.str("note"),
                p.long("box") ?: 1L,
                p.str("due_date") ?: "",
                p.str("last_review"),
                p.long("reviews") ?: 0L,
                p.long("lapses") ?: 0L,
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.ObjectiveReflection -> db.objectiveReflectionQueries.update(
                p.str("objective_id") ?: "",
                p.long("attempt") ?: 1L,
                p.str("text") ?: "",
                p.str("missed_deadline"),
                row.updatedAt,
                row.id,
            )
            EntityKind.CravingObservation -> db.cravingObservationQueries.update(
                p.str("habit") ?: "",
                p.str("date") ?: "",
                p.str("sensations") ?: "",
                p.str("zones") ?: "",
                p.long("intensity") ?: 5L,
                p.str("outcome") ?: "crossed",
                row.updatedAt,
                row.id,
            )
            EntityKind.TimerState -> db.timerStateQueries.update(
                p.long("running") ?: 0L,
                p.long("started_at"),
                p.long("planned_minutes") ?: 25L,
                p.str("item_id"),
                p.str("skill_id"),
                p.long("paused_at"),
                p.long("paused_accum_ms") ?: 0L,
                p.str("device"),
                row.updatedAt,
                row.id,
            )
            EntityKind.FocusSession -> db.focusSessionQueries.update(
                p.long("started_at") ?: 0L,
                p.long("ended_at") ?: 0L,
                p.long("focus_minutes") ?: 0L,
                p.long("break_minutes") ?: 0L,
                p.long("pomodoros") ?: 0L,
                p.str("task_id"),
                p.long("created_at") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.Objective -> {
                db.objectiveQueries.update(
                    p.str("category_id"),
                    p.str("title") ?: "",
                    p.str("description"),
                    p.str("motivation"),
                    p.str("deadline"),
                    p.long("year"),
                    p.str("status") ?: "",
                    p.long("progress") ?: 0L,
                    row.updatedAt,
                    row.id,
                )
                db.objectiveQueries.setSkillAndStep(p.str("skill_id"), p.str("step_id"), row.id)
            }
            EntityKind.Project -> {
                db.projectQueries.update(
                    p.str("organization_id") ?: "",
                    p.str("objective_id"),
                    p.str("name") ?: "",
                    p.str("description"),
                    p.str("objectives"),
                    p.str("color") ?: "",
                    p.str("icon") ?: "",
                    p.long("order_index") ?: 0L,
                    row.updatedAt,
                    row.id,
                )
                db.projectQueries.setSkill(p.str("skill_id"), row.id)
            }
            EntityKind.ProjectStatus -> db.projectStatusQueries.update(
                p.str("name") ?: "",
                p.str("color") ?: "",
                p.long("order_index") ?: 0L,
                p.long("is_terminal") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.Milestone -> db.milestoneQueries.update(
                p.str("title") ?: "",
                p.str("description"),
                p.str("deadline"),
                p.long("progress") ?: 0L,
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.Task -> {
                db.taskQueries.update(
                    p.str("parent_kind") ?: "",
                    p.str("parent_id"),
                    p.str("status_id"),
                    p.str("title") ?: "",
                    p.str("description"),
                    p.str("status") ?: "",
                    p.str("priority"),
                    p.long("effort"),
                    p.str("due_date"),
                    p.long("estimated_minutes"),
                    p.long("real_minutes"),
                    p.str("recurrence"),
                    p.str("pomodoro_pool_date"),
                    p.long("order_index") ?: 0L,
                    p.long("completed_at"),
                    row.updatedAt,
                    row.id,
                )
                db.taskQueries.setParcoursFields(p.str("kind"), p.str("deliverable"), p.str("acceptance"), row.id)
            }
            EntityKind.FinanceTransaction -> db.financeTransactionQueries.update(
                p.str("date") ?: "",
                p.str("account_id") ?: "",
                p.str("to_account_id"),
                p.str("cat_id"),
                p.str("type") ?: "",
                p.long("amount") ?: 0L,
                p.str("description"),
                p.str("fund_id"),
                row.updatedAt,
                row.id,
            )
            EntityKind.ExpenseListItem -> db.expenseListItemQueries.update(
                p.str("name") ?: "",
                p.long("planned") ?: 0L,
                p.long("actual"),
                p.str("cat_id"),
                p.long("is_done") ?: 0L,
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.PomodoroItem -> db.pomodoroItemQueries.update(
                p.str("session_id") ?: "",
                p.str("source_type") ?: "",
                p.str("source_id") ?: "",
                p.str("custom_title"),
                p.long("priority") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("completed") ?: 0L,
                p.long("completed_at"),
                p.long("estimated_minutes"),
                p.long("created_at") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.PomodoroCheck -> db.pomodoroCheckQueries.update(
                p.str("item_id") ?: "",
                p.str("label") ?: "",
                p.str("note"),
                p.long("checked") ?: 0L,
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: 0L,
                row.updatedAt,
                row.id,
            )
        }
    }

    private fun softDeleteFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val deletedAt = row.deletedAt ?: row.updatedAt
        when (entity) {
            EntityKind.Organization -> db.organizationQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Category -> db.categoryQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.UserProfile -> db.userProfileQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.FinanceAccount -> db.financeAccountQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.FinanceCategory -> db.financeCategoryQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.PomodoroSession -> db.pomodoroSessionQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.ExpenseList -> db.expenseListQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Notebook -> db.notebookQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Note -> db.noteQueries.softDeleteFromRemote(deletedAt, row.updatedAt, row.id)
            EntityKind.Skill -> db.skillQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.ParcoursStep -> db.parcoursStepQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.CompetenceFamily -> db.competenceFamilyQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Competence -> db.competenceQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.CompetenceCriterion -> db.competenceCriterionQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.PortfolioPiece -> db.portfolioPieceQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote -> MindMapSyncOps.softDeleteFromRemote(entity, db, row, deletedAt)
            EntityKind.Attachment -> FileSyncOps.softDeleteFromRemote(entity, db, row, deletedAt)
            EntityKind.Keychain, EntityKind.KeychainItem -> VaultSyncOps.softDeleteFromRemote(entity, db, row, deletedAt)
            EntityKind.ExpenseEntry -> ExpenseEntrySyncOps.softDeleteFromRemote(entity, db, row, deletedAt)
            EntityKind.Simulation, EntityKind.SimulationLine -> SimulationSyncOps.softDeleteFromRemote(entity, db, row, deletedAt)
            EntityKind.Loan, EntityKind.LoanPayment -> LoanSyncOps.softDeleteFromRemote(entity, db, row, deletedAt)
            EntityKind.SkillResource -> SkillResourceSyncOps.softDeleteFromRemote(entity, db, row, deletedAt)
            EntityKind.AssistantConversation, EntityKind.AssistantMessage -> AssistantSyncOps.softDeleteFromRemote(entity, db, row, deletedAt)
            EntityKind.PortfolioPieceCompetence -> db.portfolioPieceCompetenceQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Asset -> db.assetQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.AssetValuation -> db.assetValuationQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Flashcard -> db.flashcardQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.ObjectiveReflection -> db.objectiveReflectionQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.CravingObservation -> db.cravingObservationQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.TimerState -> db.timerStateQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.FocusSession -> db.focusSessionQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Objective -> db.objectiveQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Project -> db.projectQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.ProjectStatus -> db.projectStatusQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Milestone -> db.milestoneQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.Task -> db.taskQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.FinanceTransaction -> db.financeTransactionQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.ExpenseListItem -> db.expenseListItemQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.PomodoroItem -> db.pomodoroItemQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.PomodoroCheck -> db.pomodoroCheckQueries.softDelete(deletedAt, row.updatedAt, row.id)
        }
    }

    private fun restoreIfDeleted(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        when (entity) {
            EntityKind.Organization -> db.organizationQueries.restore(row.updatedAt, row.id)
            EntityKind.Category -> db.categoryQueries.restore(row.updatedAt, row.id)
            EntityKind.UserProfile -> db.userProfileQueries.restore(row.updatedAt, row.id)
            EntityKind.FinanceAccount -> db.financeAccountQueries.restore(row.updatedAt, row.id)
            EntityKind.FinanceCategory -> db.financeCategoryQueries.restore(row.updatedAt, row.id)
            EntityKind.PomodoroSession -> db.pomodoroSessionQueries.restore(row.updatedAt, row.id)
            EntityKind.ExpenseList -> db.expenseListQueries.restore(row.updatedAt, row.id)
            EntityKind.Notebook -> db.notebookQueries.restore(row.updatedAt, row.id)
            EntityKind.Note -> db.noteQueries.restore(row.updatedAt, row.id)
            EntityKind.Skill -> db.skillQueries.restore(row.updatedAt, row.id)
            EntityKind.ParcoursStep -> db.parcoursStepQueries.restore(row.updatedAt, row.id)
            EntityKind.CompetenceFamily -> db.competenceFamilyQueries.restore(row.updatedAt, row.id)
            EntityKind.Competence -> db.competenceQueries.restore(row.updatedAt, row.id)
            EntityKind.CompetenceCriterion -> db.competenceCriterionQueries.restore(row.updatedAt, row.id)
            EntityKind.PortfolioPiece -> db.portfolioPieceQueries.restore(row.updatedAt, row.id)
            EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote -> MindMapSyncOps.restoreIfDeleted(entity, db, row)
            EntityKind.Attachment -> FileSyncOps.restoreIfDeleted(entity, db, row)
            EntityKind.Keychain, EntityKind.KeychainItem -> VaultSyncOps.restoreIfDeleted(entity, db, row)
            EntityKind.ExpenseEntry -> ExpenseEntrySyncOps.restoreIfDeleted(entity, db, row)
            EntityKind.Simulation, EntityKind.SimulationLine -> SimulationSyncOps.restoreIfDeleted(entity, db, row)
            EntityKind.Loan, EntityKind.LoanPayment -> LoanSyncOps.restoreIfDeleted(entity, db, row)
            EntityKind.SkillResource -> SkillResourceSyncOps.restoreIfDeleted(entity, db, row)
            EntityKind.AssistantConversation, EntityKind.AssistantMessage -> AssistantSyncOps.restoreIfDeleted(entity, db, row)
            EntityKind.PortfolioPieceCompetence -> db.portfolioPieceCompetenceQueries.restore(row.updatedAt, row.id)
            EntityKind.Asset -> db.assetQueries.restore(row.updatedAt, row.id)
            EntityKind.AssetValuation -> db.assetValuationQueries.restore(row.updatedAt, row.id)
            EntityKind.Flashcard -> db.flashcardQueries.restore(row.updatedAt, row.id)
            EntityKind.ObjectiveReflection -> db.objectiveReflectionQueries.restore(row.updatedAt, row.id)
            EntityKind.CravingObservation -> db.cravingObservationQueries.restore(row.updatedAt, row.id)
            EntityKind.TimerState -> db.timerStateQueries.restore(row.updatedAt, row.id)
            EntityKind.FocusSession -> db.focusSessionQueries.restore(row.updatedAt, row.id)
            EntityKind.Objective -> db.objectiveQueries.restore(row.updatedAt, row.id)
            EntityKind.Project -> db.projectQueries.restore(row.updatedAt, row.id)
            EntityKind.ProjectStatus -> db.projectStatusQueries.restore(row.updatedAt, row.id)
            EntityKind.Milestone -> db.milestoneQueries.restore(row.updatedAt, row.id)
            EntityKind.Task -> db.taskQueries.restore(row.updatedAt, row.id)
            EntityKind.FinanceTransaction -> db.financeTransactionQueries.restore(row.updatedAt, row.id)
            EntityKind.ExpenseListItem -> db.expenseListItemQueries.restore(row.updatedAt, row.id)
            EntityKind.PomodoroItem -> db.pomodoroItemQueries.restore(row.updatedAt, row.id)
            EntityKind.PomodoroCheck -> db.pomodoroCheckQueries.restore(row.updatedAt, row.id)
        }
    }
}

internal data class LocalSnapshot(val updatedAt: Long, val isDeleted: Boolean)

internal fun Map<String, Any?>.str(key: String): String? = this[key] as? String
internal fun Map<String, Any?>.long(key: String): Long? = when (val v = this[key]) {
    is Long -> v
    is Int -> v.toLong()
    is Number -> v.toLong()
    else -> null
}
private fun Map<String, Any?>.double(key: String): Double? = when (val v = this[key]) {
    is Double -> v
    is Float -> v.toDouble()
    is Number -> v.toDouble()
    else -> null
}
